import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Check, PartyPopper, Star, Heart, ArrowRight } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`w-11 h-11 rounded-xl border transition-all grid place-items-center ${
          value >= n
            ? "bg-[#00FF88]/15 border-[#00FF88]/40 text-[#00FF88]"
            : "bg-[#0A0A0A] border-white/10 text-white/30 hover:border-white/30"
        }`}
        aria-label={`${n} star${n>1?"s":""}`}
        data-testid={`fb-star-${n}`}
      >
        <Star className={`w-5 h-5 ${value >= n ? "fill-current" : ""}`} />
      </button>
    ))}
  </div>
);

const Option = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 rounded-2xl border text-sm font-semibold transition-all ${
      active
        ? "bg-[#00FF88] text-black border-[#00FF88]"
        : "bg-[#0A0A0A] text-white/75 border-white/10 hover:border-white/30"
    }`}
  >
    {children}
  </button>
);

const Feedback = () => {
  const { appId } = useParams();
  const [stage, setStage] = useState("verify"); // verify | form | done | locked
  const [eligible, setEligible] = useState(null);
  const [email, setEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    helpfulness: 0,
    used_for: "",
    timeline_reasonable: "",
    would_use_again: "",
    would_recommend: "",
    improvements: "",
    share_anon_in_investor_materials: "",
    contact_for_testimonial: "",
  });

  const verify = async (e) => {
    e?.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter the email you used on your application.");
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/pilot/feedback/check/${appId}`, { params: { email } });
      const data = res.data;
      if (data.eligible) {
        setEligible(data);
        setStage("form");
      } else if (data.reason === "already_submitted") {
        setEligible(data);
        setStage("locked");
      } else {
        toast.error("We couldn't find an application matching that email. Double-check the email you applied with.");
      }
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  const valid =
    form.helpfulness >= 1 && form.helpfulness <= 5 &&
    form.used_for.trim().length >= 1 &&
    form.timeline_reasonable && form.would_use_again && form.would_recommend &&
    form.improvements.trim().length >= 1 &&
    form.share_anon_in_investor_materials && form.contact_for_testimonial;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please answer every question — they're all required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/pilot/feedback/${appId}`, { email, ...form });
      setStage("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="feedback-page">
      <header className="border-b border-white/5 sticky top-0 z-40 bg-[#050505]/70 backdrop-blur-xl">
        <div className="max-w-[760px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4 text-white/60" />
            <LogoMark size={28} />
            <span className="font-display font-extrabold tracking-tight">GigAfloat</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[680px] px-6 lg:px-12 py-14">
        <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">// Post-pilot feedback</div>

        {stage === "verify" && (
          <>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3">Thank you.</h1>
            <p className="text-white/65 text-lg mb-8 leading-relaxed">
              You're helping shape GigAfloat. Verify your application with the email you used, and we'll open the feedback form.
            </p>
            <form onSubmit={verify} className="space-y-4 max-w-md">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full h-12 px-4 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20"
                data-testid="fb-verify-email"
              />
              <button
                type="submit"
                disabled={verifying}
                className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow disabled:opacity-50"
                data-testid="fb-verify-submit"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-[11px] text-white/35 font-mono-c">
                Application: {appId}
              </p>
            </form>
          </>
        )}

        {stage === "locked" && (
          <>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-3">You're all set.</h1>
            <p className="text-white/65 mb-6 leading-relaxed">
              We've already received your feedback{eligible?.first_name ? `, ${eligible.first_name}` : ""}. Thank you.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow">
              Back to GigAfloat <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {stage === "done" && (
          <div className="text-center py-4">
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-[#00FF88]/30 blur-2xl animate-pulse" />
              <div className="relative w-full h-full rounded-full bg-[#0A0A0A] border border-[#00FF88]/40 grid place-items-center">
                <Heart className="w-10 h-10 text-[#00FF88] fill-[#00FF88]" />
              </div>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-3">Thank you.</h1>
            <p className="text-white/65 leading-relaxed mb-8">
              Your feedback goes straight to the founder and helps shape what we build next.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow">
              Back to GigAfloat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {stage === "form" && (
          <>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2">
              Hey {eligible?.first_name || "there"} — how&apos;d it go?
            </h1>
            <p className="text-white/55 mb-8">All 8 questions are required. Should take ~60 seconds.</p>

            <form onSubmit={submit} className="space-y-7" data-testid="fb-form">
              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">How helpful was the float?</div>
                <Stars value={form.helpfulness} onChange={(v) => setForm((f) => ({ ...f, helpfulness: v }))} testid="fb-stars" />
              </div>

              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">What did you use it for?</div>
                <textarea
                  rows={2}
                  value={form.used_for}
                  onChange={(e) => setForm((f) => ({ ...f, used_for: e.target.value.slice(0, 400) }))}
                  placeholder="Gas, oil change, etc."
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 resize-none text-sm"
                  data-testid="fb-used-for"
                />
              </div>

              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Did the repayment timeline feel reasonable?</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["yes","Yes"],["somewhat","Somewhat"],["no","No"]].map(([v, l]) => (
                    <Option key={v} active={form.timeline_reasonable === v} onClick={() => setForm((f) => ({ ...f, timeline_reasonable: v }))} testid={`fb-timeline-${v}`}>{l}</Option>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Would you use GigAfloat again?</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["yes","Yes"],["no","No"],["maybe","Maybe"]].map(([v, l]) => (
                    <Option key={v} active={form.would_use_again === v} onClick={() => setForm((f) => ({ ...f, would_use_again: v }))} testid={`fb-again-${v}`}>{l}</Option>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Would you recommend GigAfloat to another gig worker?</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["yes","Yes"],["no","No"],["maybe","Maybe"]].map(([v, l]) => (
                    <Option key={v} active={form.would_recommend === v} onClick={() => setForm((f) => ({ ...f, would_recommend: v }))} testid={`fb-recommend-${v}`}>{l}</Option>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">What would make GigAfloat better?</div>
                <textarea
                  rows={3}
                  value={form.improvements}
                  onChange={(e) => setForm((f) => ({ ...f, improvements: e.target.value.slice(0, 1000) }))}
                  placeholder="Be honest — this is what shapes what we build."
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 resize-y text-sm"
                  data-testid="fb-improvements"
                />
              </div>

              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Can we use your feedback anonymously in investor materials?</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["yes","Yes"],["no","No"]].map(([v, l]) => (
                    <Option key={v} active={form.share_anon_in_investor_materials === v} onClick={() => setForm((f) => ({ ...f, share_anon_in_investor_materials: v }))} testid={`fb-anon-${v}`}>{l}</Option>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Can we contact you for a testimonial?</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["yes","Yes"],["no","No"]].map(([v, l]) => (
                    <Option key={v} active={form.contact_for_testimonial === v} onClick={() => setForm((f) => ({ ...f, contact_for_testimonial: v }))} testid={`fb-test-${v}`}>{l}</Option>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !valid}
                className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-base px-8 py-4 rounded-full btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="fb-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send feedback <Check className="w-5 h-5" /></>}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
};

export default Feedback;
